import {resolveTrackTitleDecoration, canonicalTrackTitle} from "./trackTitleDecoration";

const TITLE_SYMBOLS = Object.freeze({
  sold: "★",
  original: "🔥",
  remix: "❤️",
  halfOff: "$",
  freeLease: "🛒",
  special: "🌿",
});

function explicitBoolean(value) {
  return typeof value === "boolean" ? value : null;
}

/**
 * Central compatibility layer for the title markers used by older track data.
 * Structured fields win when they exist. Title parsing remains only so current
 * catalog documents keep their existing behavior until a deliberate migration.
 */
export function getTrackCommerceFlags(track = {}) {
  const title = String(track.title || "").trim();
  const decoration = resolveTrackTitleDecoration(track);
  const decoratedAs = (icon) => decoration.enabled && decoration.icon === icon;
  const availability = String(track.availability || "").trim().toLowerCase();
  const salePercent = Number(track.salePercent);

  const structured = {
    sold: explicitBoolean(track.isSold),
    original: explicitBoolean(track.isOriginal),
    remix: explicitBoolean(track.isRemix),
    halfOff: explicitBoolean(track.isHalfOff),
    freeLease: explicitBoolean(track.isFreeLease),
    special: explicitBoolean(track.isSpecial),
  };

  return {
    // Sold is monotonic: any sold signal wins. This also keeps legacy ★ sales
    // closed when an older document still contains isSold=false.
    sold: structured.sold === true || availability === "sold" || decoratedAs(TITLE_SYMBOLS.sold) || title.startsWith(TITLE_SYMBOLS.sold),
    original: structured.original ?? (decoratedAs(TITLE_SYMBOLS.original) || title.endsWith(TITLE_SYMBOLS.original)),
    remix: structured.remix ?? (decoratedAs(TITLE_SYMBOLS.remix) || title.endsWith(TITLE_SYMBOLS.remix) || title.endsWith("❤")),
    halfOff: structured.halfOff ?? (salePercent === 50 || decoratedAs(TITLE_SYMBOLS.halfOff) || title.includes(TITLE_SYMBOLS.halfOff)),
    freeLease: structured.freeLease ?? (decoratedAs(TITLE_SYMBOLS.freeLease) || title.includes(TITLE_SYMBOLS.freeLease)),
    special: structured.special ?? (decoratedAs(TITLE_SYMBOLS.special) || title.includes(TITLE_SYMBOLS.special)),
  };
}

export function isTrackUnavailable(track) {
  const flags = getTrackCommerceFlags(track);
  return flags.sold || flags.remix || track?.availableForSale === false;
}

export function isExclusiveUnavailable(track) {
  const flags = getTrackCommerceFlags(track);
  return isTrackUnavailable(track) || flags.original || track?.exclusiveAvailable === false;
}

export function stripLegacyTrackDecorations(value) {
  return canonicalTrackTitle(String(value || "").replace(/\s*\(ex\)\s*$/i, ""));
}

export function stripLegacyExclusiveSuffix(value) {
  return String(value || "").replace(/\s*\(ex\)\s*$/i, "").trim();
}

export function hasLegacyTrackDecoration(track) {
  const title = String(track?.title || "");
  return /[★🔥🛒🌿❤\uFE0F$]/u.test(title) || /\s*\(ex\)\s*$/i.test(title);
}
